<template>
  <loading-content :loading="loading" :class="{ 'obj-card obj-pad': plate }">
    <div v-if="photos.length <= 0 && !loading" class="text-grey">Fotosid pole</div>
    <v-row v-else>
      <v-col
        v-for="photo in photos"
        :key="photoKey(photo)"
        cols="6" sm="4" md="3" lg="2"
      >
        <v-card>
          <v-img
            :src="errored[photoKey(photo)] ? logo : url(photo)"
            :contain="!!errored[photoKey(photo)]"
            :aspect-ratio="1"
            :class="{ 'bg-grey-lighten-3': errored[photoKey(photo)] }"
            style="cursor: pointer"
            @error="onError(photo)"
            @click="open(photo)"
          >
            <!-- variant="text": a Vuetify 3 icon button is a raised one unless
                 told otherwise; Vuetify 2's `icon` was flat, on the dark
                 background the style gives it. -->
            <v-btn
              v-if="canDelete"
              icon
              variant="text"
              color="white"
              size="x-small"
              class="ma-1"
              style="position: absolute; top: 0; right: 0; background: rgba(0,0,0,0.5)"
              :loading="deleting === photoKey(photo)"
              @click.stop="confirmDelete = photo"
            >
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </v-img>
          <v-card-text class="pa-2 text-caption">
            <div class="text-truncate">
              <strong>{{photo.creator && photo.creator.fullName}}</strong>
              <!-- This one came in with a signed akt, so deleting it here takes
                   it away from the act as well. -->
              <span v-if="photo.fromAkt" class="text-grey ml-1">(akt)</span>
            </div>
            <div class="text-grey">{{format(photo.entryDate)}}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- Centred, as Vuetify 2's overlay drew its content by itself. A click
         beside the photo or Esc closes it too, which is Vuetify 3's own way. -->
    <v-overlay
      :model-value="!!overlay"
      class="align-center justify-center"
      @update:model-value="open => open || (overlay = false)"
    >
      <div class="text-center" style="width: 100vw">
        <v-btn icon variant="text" color="white" @click="overlay = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <img v-if="overlay" @click="overlay = false" :src="overlay" style="max-width:90%;max-height:88vh;object-fit:contain;margin:auto;display:block;" />
      </div>
    </v-overlay>
    <confirm
      :visible="!!confirmDelete"
      title="Kustuta foto?"
      text="Foto kustutatakse märkuse juurest ja kliendiportaalist."
      action="Kustuta foto"
      @confirm="remove(confirmDelete)"
      @cancel="confirmDelete = null"
    >
      Kas oled kindel, et soovid selle foto jäädavalt kustutada?
      <!-- Signing an akt files its field photos as a märkus on the object, so
           the same file shows up here. Deleting it here takes it away from the
           akt too. -->
      <v-alert type="warning" class="mt-3 mb-0" variant="tonal" density="compact">
        Fotod võivad olla seotud aktidega!
      </v-alert>
    </confirm>
  </loading-content>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import logo from '@/assets/gaspre-logo.png'

export default {
  props: {
    // The object page's new look (Seaded -> Objekt uues kujunduses): the same
    // content in the new card. overview is View.vue's, for the count.
    plate: {
      type: Boolean,
      default: false
    },
    overview: {
      type: Object,
      default: null
    },
    object: {
      required: true
    }
  },
  data () {
    return {
      loading: true,
      overlay: false,
      photos: [],
      errored: {},
      logo,
      confirmDelete: null,
      deleting: null
    }
  },
  computed: {
    objectId () {
      return this.$route.params.objectId
    },
    canDelete () {
      return this.$hasRole(['admin', 'root'])
    }
  },
  watch: {
    objectId () {
      this.refresh()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    photoKey (photo) {
      return photo.noteId + '-' + photo.picture
    },
    url (photo) {
      return '/api/object/' + this.objectId + '/note/' + photo.noteId + '/photo?picture=' + photo.picture
    },
    onError (photo) {
      this.errored[this.photoKey(photo)] = true
    },
    open (photo) {
      // Don't open the lightbox for a missing image - the placeholder isn't worth enlarging
      if (this.errored[this.photoKey(photo)]) {
        return
      }
      this.overlay = this.url(photo)
    },
    format (date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : ''
    },
    async remove (photo) {
      const key = this.photoKey(photo)
      this.confirmDelete = null
      this.deleting = key
      try {
        await api.deleteObjectPhoto(this.objectId, photo.noteId, photo.picture)
        this.showNotification({ text: 'Foto kustutatud', color: 'green' })
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.deleting = null
    },
    async refresh () {
      // Leaving the page clears the route's objectId before this tab is gone.
      if (!this.objectId) {
        return
      }
      this.loading = true
      this.errored = {}
      try {
        const { photos } = await api.getObjectPhotos(this.objectId)
        this.photos = photos
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>
